// src/pages/Training/TrainingTaskModal.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ModalManager from "@restart/ui/ModalManager";
import axios from "axios";
import "../../styles/trainingQuiz.css";

const letterOf = (i) => String.fromCharCode(65 + i);
const trainingModalManager = new ModalManager({
  handleContainerOverflow: false,
});

// -------- helpers for media --------
const isMp4 = (url) => !!url && /\.mp4($|\?)/i.test(url);
const isPdf = (url) => !!url && /\.pdf($|\?)/i.test(url);
const isHttp = (url) => /^https?:\/\//i.test(url);
const normalizeAssetUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const v = raw.trim();
  const base = process.env.REACT_APP_API_BASE_URL || "";
  if (isHttp(v)) return v;
  if (v.startsWith("/training-files")) return `${base}${v}`;
  if (isMp4(v) || isPdf(v)) return `${base}/training-files/${v}`;
  return v;
};
const TrainingTaskModal = ({
  show,
  onHide,
  moduleKey,
  title,
  userId,
  tpStatus,
  tpScore,
  onSubmitted,
}) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const isAdmin = localStorage.getItem("userRole") === "1";
  const normalizedStatus = String(tpStatus || "").toLowerCase();

  // single modal mode; swap content internally
  const [mode, setMode] = useState(null); // 'quiz' | 'results'
  const [ready, setReady] = useState(false);

  const [videoUrl, setVideoUrl] = useState(null);
  const [docUrl, setDocUrl] = useState(null); // PDF
  const [instructions, setInstructions] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [passingScore, setPassingScore] = useState(50);
  const [isSettingRedo, setIsSettingRedo] = useState(false);
  const draftSaveTimerRef = useRef(null);
  const latestAnswersRef = useRef({});
  const draftDirtyRef = useRef(false);
  const draftSaveChainRef = useRef(Promise.resolve());

  // NEW: which part of the quiz flow the learner is in
  // 'video' -> 'pdf' -> 'questions' (the last step is just for safety when no media)
  const [quizStage, setQuizStage] = useState("questions"); // default; will be corrected after data load

  const total = questions.length;

  const answered = useMemo(
    () =>
      questions.reduce((count, q) => {
        const val = answers[q.id];
        if (q.multi) return count + (Array.isArray(val) && val.length ? 1 : 0);
        return count + (val ? 1 : 0);
      }, 0),
    [answers, questions]
  );

  const loadData = useCallback(async () => {
    const res = await axios.get(
      `${BASE_URL}/api/trainings/module/${moduleKey}/questions`,
      {
        params: { userId },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    return res.data;
  }, [BASE_URL, moduleKey, userId]);

  const persistDraft = useCallback(
    (draftAnswers) => {
      const serializedAnswers = Object.entries(draftAnswers)
        .map(([questionId, selected]) => ({
          questionId: Number(questionId),
          selected: Array.isArray(selected) ? selected : [selected],
        }))
        .filter((answer) => answer.selected.filter(Boolean).length > 0);

      draftSaveChainRef.current = draftSaveChainRef.current
        .catch(() => undefined)
        .then(() =>
          axios.post(
            `${BASE_URL}/api/trainings/draft`,
            {
              moduleKey,
              userId,
              answers: serializedAnswers,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          ),
        )
        .catch((error) => {
          console.error("Failed to auto-save training answers", error);
        });

      return draftSaveChainRef.current;
    },
    [BASE_URL, moduleKey, userId],
  );

  const queueDraftSave = useCallback(
    (draftAnswers) => {
      latestAnswersRef.current = draftAnswers;
      draftDirtyRef.current = true;
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
      draftSaveTimerRef.current = setTimeout(() => {
        draftSaveTimerRef.current = null;
        draftDirtyRef.current = false;
        persistDraft(latestAnswersRef.current);
      }, 400);
    },
    [persistDraft],
  );

  useEffect(
    () => () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
      if (draftDirtyRef.current) {
        draftDirtyRef.current = false;
        persistDraft(latestAnswersRef.current);
      }
    },
    [persistDraft],
  );

  useEffect(() => {
    if (!show || !moduleKey) return;

    const wantsResults = ["complete", "verified", "redo", "expired"].includes(
      normalizedStatus,
    );

    // reset before async load to avoid any flash
    setMode(null);
    setReady(false);
    setAnswers({});
    setQuestions([]);
    setVideoUrl(null);
    setDocUrl(null);
    setInstructions("");
    setResult(null);
    setPassingScore(50);
    setQuizStage("questions");
    latestAnswersRef.current = {};
    draftDirtyRef.current = false;

    (async () => {
      try {
        const data = await loadData();
        const v = normalizeAssetUrl(data.videoUrl, moduleKey, "video");
        const d = normalizeAssetUrl(data.docUrl, moduleKey, "pdf");

        setQuestions(data.questions || []);
        setVideoUrl(v);
        setDocUrl(d);
        setInstructions(String(data.instructions || "").trim());
        setPassingScore(Number(data.passingScore ?? 50));

        const saved = (data.progress && data.progress.answers) || [];
        const questionById = new Map(
          (data.questions || []).map((question) => [question.id, question]),
        );
        const restoredAnswers = {};
        saved.forEach((answer) => {
          const question = questionById.get(Number(answer.questionId));
          const selected = Array.isArray(answer.selected)
            ? answer.selected.map(String)
            : [answer.selected].filter(Boolean).map(String);
          if (!question || selected.length === 0) return;
          restoredAnswers[question.id] = question.multi
            ? selected
            : selected[0];
        });
        latestAnswersRef.current = restoredAnswers;
        if (!wantsResults) {
          setAnswers(restoredAnswers);
        }

        // establish starting quiz stage: video -> pdf -> questions
        if (!wantsResults) {
          if (v) setQuizStage("video");
          else if (d) setQuizStage("pdf");
          else setQuizStage("questions");
        }

        if (wantsResults) {
          const byQ = new Map(
            saved.map((a) => [Number(a.questionId), a.selected || []])
          );

          const norm = (arr) =>
            Array.isArray(arr) ? arr.map(String).sort() : [String(arr)].sort();

          const detail = (data.questions || []).map((q) => {
            const selected = byQ.get(q.id) || [];
            const correct = q.correct || [];
            const isCorrect =
              JSON.stringify(norm(selected)) === JSON.stringify(norm(correct));
            return { questionId: q.id, selected, correct, isCorrect };
          });

          const correctCount = detail.filter((d0) => d0.isCorrect).length;
          const score = Number(data.progress?.score ?? tpScore ?? 0);

          setResult({
            total: data.questions.length,
            correct: correctCount,
            score,
            status: data.progress?.status || normalizedStatus,
            detail,
          });
        }

        // Reveal the modal content only after data is ready (prevents "first window" flash)
        setMode(wantsResults ? "results" : "quiz");
      } catch (e) {
        console.error("Failed to load training module", e);
      } finally {
        setReady(true);
      }
    })();
  }, [show, moduleKey, tpStatus, tpScore, loadData, normalizedStatus]);

  const handleSelect = (q, letter) => {
    const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
    const next = !q.multi
      ? { ...answers, [q.id]: letter }
      : {
          ...answers,
          [q.id]: current.includes(letter)
            ? current.filter((item) => item !== letter)
            : [...current, letter],
        };
    setAnswers(next);
    queueDraftSave(next);
  };

  const handleSubmit = async () => {
    try {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
      draftDirtyRef.current = false;
      await persistDraft(answers);

      const payload = {
        moduleKey,
        userId,
        answers: Object.entries(answers).map(([qid, sel]) => ({
          questionId: Number(qid),
          selected: Array.isArray(sel) ? sel : [sel],
        })),
      };

      const res = await axios.post(
        `${BASE_URL}/api/trainings/submit`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      setResult(res.data);
      setPassingScore(Number(res.data.passingScore ?? passingScore));
      setMode("results"); // results step: no media
    } catch (err) {
      console.error("Submission failed", err);
      alert("Failed to submit answers.");
    }
  };

  const handleRedo = async () => {
    const confirmed = window.confirm(
      "Start this training again? A new attempt will be created and the questions will be reshuffled.",
    );
    if (!confirmed) return;

    try {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
      draftDirtyRef.current = false;
      await axios.post(
        `${BASE_URL}/api/trainings/redo`,
        { moduleKey, userId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const refreshed = await loadData();

      const emptyAnswers = {};
      setQuestions(refreshed.questions || []);
      setAnswers(emptyAnswers);
      latestAnswersRef.current = emptyAnswers;
      setResult(null);
      setQuizStage(videoUrl ? "video" : docUrl ? "pdf" : "questions");
      setMode("quiz");
    } catch (error) {
      console.error("Failed to start training re-do", error);
      alert(
        error.response?.data?.message || "Failed to start the training re-do.",
      );
    }
  };

  const handleAdminSetRedo = async () => {
    const confirmed = window.confirm(
      "Set this training as Re-do? The user can take it again immediately.",
    );
    if (!confirmed) return;

    setIsSettingRedo(true);
    try {
      await axios.post(
        `${BASE_URL}/api/trainings/redo`,
        { moduleKey, userId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (onSubmitted) onSubmitted();
      else onHide();
    } catch (error) {
      console.error("Failed to set training as re-do", error);
      alert(
        error.response?.data?.message || "Failed to set the training as re-do.",
      );
    } finally {
      setIsSettingRedo(false);
    }
  };

  const handleClose = async () => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    if (draftDirtyRef.current) {
      draftDirtyRef.current = false;
      await persistDraft(latestAnswersRef.current);
    }
    onHide();
  };

  // ---- Renderers for media ----
  const VideoBlock = ({ url }) => {
    if (!url) return null;
    return isMp4(url) ? (
      <video
        controls
        src={url}
        preload="metadata"
        className="tq-video-el"
        onError={(e) => e.currentTarget.closest(".tq-video")?.remove()}
      />
    ) : (
      <iframe
        title="training-video"
        src={url}
        className="tq-video-el"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  };

  const PdfBlock = ({ url }) => {
    if (!url) return null;
    return (
      <iframe
        title="training-pdf"
        className="tq-pdf-el"
        src={isPdf(url) ? url : `${url}#view=FitH`}
      />
    );
  };

  if (!show || !ready || !mode) return null;
  const resultStatus = String(result?.status || normalizedStatus).toLowerCase();
  const requiresRedo =
    resultStatus === "expired" ||
    Number(result?.score) < Number(passingScore);

  const showVideo = mode === "quiz" && quizStage === "video" && !!videoUrl;
  const showPdf = mode === "quiz" && quizStage === "pdf" && !!docUrl;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      animation={false}
      className="training-task-modal-shell"
      manager={trainingModalManager}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Media only during quiz; results never show media */}
        {mode === "quiz" && (showVideo || showPdf) && (
          <div className="tq-media mb-3">
            {showVideo && (
              <div className="tq-video mb-3">
                <VideoBlock url={videoUrl} />
              </div>
            )}
            {showPdf && (
              <div className="tq-pdf mb-3">
                <PdfBlock url={docUrl} />
              </div>
            )}
          </div>
        )}

        {mode === "quiz" && instructions && (
          <section className="training-module-instructions mb-3" role="note">
            <div className="training-module-instructions-title">
              Instructions
            </div>
            <div className="training-module-instructions-text">
              {instructions}
            </div>
          </section>
        )}

        {mode === "quiz" && (
          <>
            <div className="tq-section-title">Training Questions</div>
            <div className="tq-meta">
              {answered} / {total} answered
            </div>

            {questions.map((q, idx) => {
              const val = answers[q.id];
              return (
                <div key={q.id} className="tq-question">
                  <div className="d-flex align-items-start">
                    <span className="tq-num">{idx + 1}.</span>
                    <div className="tq-qtext">{q.question}</div>
                  </div>

                  <ul className="tq-options">
                    {q.options.map((opt, i) => {
                      const letter = letterOf(i);
                      const name = `q-${q.id}`;
                      const checked = q.multi
                        ? Array.isArray(val) && val.includes(letter)
                        : val === letter;
                      return (
                        <li className="tq-option" key={i}>
                          <input
                            className="form-check-input"
                            type={q.multi ? "checkbox" : "radio"}
                            name={name}
                            id={`${name}-${letter}`}
                            checked={!!checked}
                            onChange={() => handleSelect(q, letter)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`${name}-${letter}`}
                          >
                            {opt}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </>
        )}

        {mode === "results" && result && (
          <>
            <div className="tq-score mb-2">
              <strong>Score:</strong> {result.score}% ({result.correct}/
              {result.total})
            </div>

            {!isAdmin &&
              requiresRedo && (
                <div className="training-results-protected-note">
                  Questions and answers are hidden until you start the re-do.
                </div>
              )}
            <div
              className={
                !isAdmin &&
                requiresRedo
                  ? "training-results-protected"
                  : ""
              }
            >
            {result.detail.map((d, idx) => {
              const q = questions.find((x) => x.id === d.questionId);
              if (!q) return null;
              const showCorrectAnswers =
                Number(result.score) >= Number(passingScore);
              const listItems = (q.options || []).map((opt, i) => {
                const letter = letterOf(i);
                const sel = d.selected.includes(letter);
                const good = d.correct.includes(letter);
                const showThisCorrectAnswer =
                  good && (showCorrectAnswers || sel);
                const cls =
                  showThisCorrectAnswer
                    ? "tq-correct"
                    : sel && !good
                      ? "tq-wrong"
                      : "";
                return (
                  <li key={i} className={`tq-option ${cls}`}>
                    <span className="me-2">{letter}.</span>
                    <span>
                      {opt}
                      {showThisCorrectAnswer && " ✅"}
                      {sel && !good && " ❌"}
                    </span>
                  </li>
                );
              });

              return (
                <div key={d.questionId} className="tq-question">
                  <div className="d-flex align-items-start">
                    <span className="tq-num">{idx + 1}</span>
                    <div className="tq-qtext">{q.question}</div>
                  </div>
                  <ul className="tq-options">{listItems}</ul>
                </div>
              );
            })}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {mode === "quiz" ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>

            {/* Flow controls */}
            {quizStage === "video" && (
              <>
                {docUrl ? (
                  <Button
                    variant="outline-primary"
                    onClick={() => setQuizStage("pdf")}
                  >
                    Next: PDF
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={total > 0 && answered === 0}
                  >
                    Submit
                  </Button>
                )}
              </>
            )}

            {quizStage === "pdf" && (
              <>
                {videoUrl && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setQuizStage("video")}
                  >
                    Back to Video
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={total > 0 && answered === 0}
                >
                  Submit
                </Button>
              </>
            )}

            {quizStage === "questions" && (
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={total > 0 && answered === 0}
              >
                Submit
              </Button>
            )}
          </>
        ) : (
          <>
            {result && isAdmin ? (
              <>
                <Button
                  variant="danger"
                  onClick={handleAdminSetRedo}
                  disabled={isSettingRedo}
                >
                  {isSettingRedo ? "Setting..." : "Set as Re-do"}
                </Button>
                <Button variant="primary" onClick={handleClose}>
                  Close
                </Button>
              </>
            ) : result && requiresRedo ? (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="warning" onClick={handleRedo}>
                  Re-do
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  if (onSubmitted) onSubmitted();
                }}
              >
                Close
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TrainingTaskModal;
